/**
 * The template string, split for display. Storage never changes — a value is
 * still `{{payload.severity}}` on the wire — but nobody should have to type or
 * repair that by hand: one deleted brace used to send the literal text and you
 * found out from a run.
 */
import type { PickerVariable } from './definitionEdit.js';

export type TokenSegment =
  | { kind: 'text'; text: string }
  | { kind: 'value'; path: string; label: string; known: boolean };

const TOKEN = /\{\{\s*([^}]+?)\s*\}\}/g;

/**
 * `known: false` marks a value this step can't actually resolve — a leftover
 * from a different trigger, or a step that was renamed. It renders struck
 * through, which is the whole point: the mistake is visible before saving
 * rather than after a run.
 */
export const parseTemplate = (value: string, vars: PickerVariable[]): TokenSegment[] => {
  const byPath = new Map(vars.map((v) => [v.path, v.label]));
  const out: TokenSegment[] = [];
  let last = 0;
  for (const m of value.matchAll(TOKEN)) {
    const at = m.index;
    if (at > last) out.push({ kind: 'text', text: value.slice(last, at) });
    const path = m[1] ?? '';
    const label = byPath.get(path);
    out.push({ kind: 'value', path, label: label ?? path, known: label !== undefined });
    last = at + m[0].length;
  }
  if (last < value.length) out.push({ kind: 'text', text: value.slice(last) });
  return out;
};

/** Every value the text refers to that the step can't resolve. */
export const unknownPaths = (value: string, vars: PickerVariable[]): string[] =>
  parseTemplate(value, vars)
    .filter((s): s is Extract<TokenSegment, { kind: 'value' }> => s.kind === 'value' && !s.known)
    .map((s) => s.path);

/**
 * The DOM back to storage. A chip is one element carrying its path, so
 * backspacing it removes the whole value — the browser can't leave half a
 * reference behind the way a text field could.
 */
export const serializeNodes = (root: HTMLElement): string => {
  let out = '';
  const walk = (node: Node): void => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent ?? '';
        continue;
      }
      if (!(child instanceof HTMLElement)) continue;
      const path = child.dataset['path'];
      if (path !== undefined) {
        out += `{{${path}}}`;
        continue;
      }
      if (child.tagName === 'BR') {
        out += '\n';
        continue;
      }
      // A pasted <div>/<p> from another app is a line break plus its text.
      if (out !== '' && !out.endsWith('\n') && /^(DIV|P)$/.test(child.tagName)) out += '\n';
      walk(child);
    }
  };
  walk(root);
  return out;
};
